// Type-only import (common/common at runtime would be circular via config).
import type { TimeFrameValue } from 'common/types';

/**
 * Timeframe picker for casinos whose object carries `insightInstance`.
 * casinoInsight has no current-hour window: only closed windows below.
 * `id` is the FE timeframe id the GetCasinoInsight* routes accept;
 * `apiValue` is the casinoInsight timeframe_value it resolves to.
 */
export interface IInsightTimeframe {
  id: number;
  value: TimeFrameValue;
  apiValue: 'hours1' | 'hours4' | 'hours8' | 'hours24' | 'weeks1' | 'months1';
  label: string;
  description: string;
  bucketType: 'hour' | 'day';
}

export const INSIGHT_TIMEFRAMES: IInsightTimeframe[] = [
  {
    id: 1,
    value: 'hours1' as TimeFrameValue,
    apiValue: 'hours1',
    label: '1h',
    description: 'Last 1 Hour',
    bucketType: 'hour',
  },
  {
    id: 2,
    value: 'hours4' as TimeFrameValue,
    apiValue: 'hours4',
    label: '4h',
    description: 'Last 4 Hours',
    bucketType: 'hour',
  },
  {
    id: 3,
    value: 'hours8' as TimeFrameValue,
    apiValue: 'hours8',
    label: '8h',
    description: 'Last 8 Hours',
    bucketType: 'hour',
  },
  {
    id: 4,
    value: 'hours24' as TimeFrameValue,
    apiValue: 'hours24',
    label: '24h',
    description: 'Last 24 Hours',
    bucketType: 'hour',
  },
  {
    id: 5,
    value: 'weeks1' as TimeFrameValue,
    apiValue: 'weeks1',
    label: '7d',
    description: 'Last 7 Days',
    bucketType: 'day',
  },
  {
    id: 6,
    value: 'months1' as TimeFrameValue,
    apiValue: 'months1',
    label: '30d',
    description: 'Last 30 Days',
    bucketType: 'day',
  },
];

/**
 * Resolver fallback (24h) for an empty/unknown id passed to
 * resolveInsightTimeframe (services/casino.ts insight requests) and the EGM
 * Details graph's stale-1h snap (DetailReportV2). NOT the Explore default: the
 * Explore entry default for insight casinos is applicationConfigurations
 * exploreDefaultTimeframeInsightCasino (lib/exploreDefaults), falling back to
 * 7d (decision Miller, Nov 3, 2026), and Explore coerces unsupported
 * timeframes to that default, not to this value.
 */
export const INSIGHT_DEFAULT_TIMEFRAME: IInsightTimeframe = INSIGHT_TIMEFRAMES[3];

/** Resolve an FE timeframe id or value to an insight timeframe; anything else -> 24h default. */
export const resolveInsightTimeframe = (idOrValue?: number | string | null): IInsightTimeframe => {
  if (idOrValue === undefined || idOrValue === null || idOrValue === '')
    return INSIGHT_DEFAULT_TIMEFRAME;
  const asNum = Number(idOrValue);
  const hit = INSIGHT_TIMEFRAMES.find((t) =>
    Number.isFinite(asNum) && String(idOrValue).trim() !== '' && /^[0-9]+$/.test(String(idOrValue))
      ? t.id === asNum
      : t.value === idOrValue || t.apiValue === idOrValue
  );
  return hit ?? INSIGHT_DEFAULT_TIMEFRAME;
};

export const isInsightTimeframeValue = (value?: string | null): boolean =>
  !!value && INSIGHT_TIMEFRAMES.some((t) => t.value === value);
